import React, { useState, useEffect } from 'react';

import * as Yup from 'yup';

import { Formik, Form, Field } from 'formik';
import { Form as BootstrapForm } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { ErrorNotification } from '~/pages/Auth/ErrorNotification';

import ClearIcon from '@material-ui/icons/Clear';
import DoneIcon from '@material-ui/icons/Done';

import {
  Textfield,
  ErrorMessage,
  EyeIcon,
  DivField,
  Label,
  SignInButton,
  LoadingIcon,
  PasswordCondition,
  LoginSubTitle,
} from './styles';
import api from '~/services/api';

function CreatePassword() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userId, setUserId] = useState('');
  const [contactId, setContactId] = useState('');
  const [token, setToken] = useState('');
  const [errorApi, setErrorApi] = useState(false);

  const regex =
    /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!"#$%&'()*+,\-.\/:;<=>?@[\\\]^_`{|}~])(?=.{8,})/;
  const CreatePasswordSchema = Yup.object().shape({
    password: Yup.string()
      .required('Insira uma senha')
      .matches(
        regex,
        'Use oito ou mais caracteres com uma combinação de letras, números e símbolos',
      ),
    passwordConfirmation: Yup.string()
      .required('Insira a confirmação de sua senha')
      .oneOf([Yup.ref('password'), null], 'As senhas não combinam'),
  });

  useEffect(() => {
    var url = new URL(window.location.href);
    setUserId(url.searchParams.get('user-id'));
    setToken(url.searchParams.get('token'));
    setContactId(url.searchParams.get('contact-id'));
  }, []);

  async function getAccessToken(values) {
    const header = {
      'Content-Type': 'application/json',
      'Accept-Language': 'pt-BR',
      Authorization: process.env.REACT_APP_SIGNIN_AUTHORIZATION,
      client_id: process.env.REACT_APP_CLIENT_ID,
    };
    const parameters = {
      method: 'POST',
      headers: header,
    };
    const body = {
      grant_type: 'client_credentials',
    };

    await api
      .post('residential/store/v1/users/authentications', body, parameters)
      .then(res => {
        createPassword(values, res.data.access_token);
      })
      .catch(err => {
        console.log('err => ', err);
        setErrorApi(true);
      });
  }

  async function createPassword(values, access_token) {
    const header = {
      'Content-Type': 'application/json',
      access_token,
    };
    const parameters = {
      method: 'PATCH',
      headers: header,
    };
    api
      .post(
        `/residential/store/v1/users/${userId}/passwords`,
        {
          contactId,
          password: values.password,
          passwordConfirmation: values.passwordConfirmation,
          token: token,
        },
        parameters,
      )
      .then(() => {
        setLoading(false);
        toast.success('Senha criada com sucesso');
        dispatch({ type: 'STEP_EMPLOYEE', step: 2 });
      })
      .catch(err => {
        console.log('err => ', err);
        setLoading(false);
        setErrorApi(true);
      });
  }

  const hasValidationError = (value, regex) => !value.match(regex);
  const areAllConditionsMet = values => {
    return (
      !hasValidationError(values.password, /.{10}/) &&
      !hasValidationError(values.password, /[A-Z]/) &&
      !hasValidationError(values.password, /[a-z]/) &&
      !hasValidationError(values.password, /[0-9]/) &&
      !hasValidationError(values.password, /[!@#$%^&*(),.?":{}|<>]/) &&
      values.password === values.passwordConfirmation &&
      values.password !== '' &&
      values.passwordConfirmation !== ''
    );
  };

  return (
    <>
      {errorApi ? (
        <ErrorNotification isMFA={false} />
      ) : (
        <Formik
          initialValues={{ password: '' }}
          onSubmit={values => {
            setLoading(true);
            getAccessToken(values);
          }}
          validationSchema={CreatePasswordSchema}
        >
          {({ handleChange, handleBlur, values, errors, touched }) => (
            <Form style={{ width: '100%' }} autocomplete="off">
              <BootstrapForm.Group controlId="password">
                <Label>Senha</Label>
                <DivField errorCondition={errors.password && touched.password}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    label="Senha"
                    arial-label="Senha"
                    aria-required
                    errorMessage={errors.password}
                    title="Senha"
                    onBlur={handleBlur}
                    placeholder="Senha"
                    value={values.password}
                    onChange={handleChange}
                    style={{
                      paddingRight: 47,
                      borderWidth: 0,
                      width: '100%',
                    }}
                  />
                  <EyeIcon
                    className={showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'}
                    errorCondition={errors.email && touched.email}
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </DivField>
                {errors.password && touched.password ? (
                  <ErrorMessage>{errors.password}</ErrorMessage>
                ) : null}
              </BootstrapForm.Group>
              <BootstrapForm.Group controlId="passwordConfirmation">
                <Label>Confirmação de Senha</Label>
                <Field
                  render={() => {
                    return (
                      <Textfield
                        id="passwordConfirmation"
                        type="password"
                        label="Confirme a Senha"
                        arial-label="Confirme a Senha"
                        aria-required
                        errorCondition={
                          errors.passwordConfirmation &&
                          touched.passwordConfirmation
                        }
                        errorMessage={errors.passwordConfirmation}
                        title="Confirme a Senha"
                        onBlur={handleBlur}
                        placeholder="Confirme a Senha"
                        value={values.passwordConfirmation}
                        onChange={handleChange}
                      />
                    );
                  }}
                  name="passwordConfirmation"
                />
                {errors.passwordConfirmation && touched.passwordConfirmation ? (
                  <ErrorMessage>{errors.passwordConfirmation}</ErrorMessage>
                ) : null}
              </BootstrapForm.Group>
              <LoginSubTitle style={{ color: '#000FFF' }} className="mb-10">
                Atenção! A senha cadastrada deverá ter:
              </LoginSubTitle>
              <PasswordCondition
                className="mb-0"
                style={{
                  color: hasValidationError(values.password, /.{10}/)
                    ? 'red'
                    : 'black',
                }}
              >
                No mínimo 10 caracteres
                {hasValidationError(values.password, /.{10}/) ? (
                  <ClearIcon
                    style={{ color: 'red', marginLeft: 2, fontSize: '18px' }}
                  />
                ) : (
                  <DoneIcon
                    style={{
                      color: '#000FFF',
                      marginLeft: 2,
                      fontSize: '18px',
                    }}
                  />
                )}
              </PasswordCondition>
              <PasswordCondition
                className="mb-0"
                style={{
                  color: hasValidationError(values.password, /[A-Z]/)
                    ? 'red'
                    : 'black',
                }}
              >
                No mínimo uma letra maiúscula
                {hasValidationError(values.password, /[A-Z]/) ? (
                  <ClearIcon
                    style={{ color: 'red', marginLeft: 2, fontSize: '18px' }}
                  />
                ) : (
                  <DoneIcon
                    style={{
                      color: '#000FFF',
                      marginLeft: 2,
                      fontSize: '18px',
                    }}
                  />
                )}
              </PasswordCondition>
              <PasswordCondition
                className="mb-0"
                style={{
                  color: hasValidationError(values.password, /[a-z]/)
                    ? 'red'
                    : 'black',
                }}
              >
                No mínimo uma letra minúscula
                {hasValidationError(values.password, /[a-z]/) ? (
                  <ClearIcon
                    style={{ color: 'red', marginLeft: 2, fontSize: '18px' }}
                  />
                ) : (
                  <DoneIcon
                    style={{
                      color: '#000FFF',
                      marginLeft: 2,
                      fontSize: '18px',
                    }}
                  />
                )}
              </PasswordCondition>
              <PasswordCondition
                className="mb-0"
                style={{
                  color: hasValidationError(values.password, /[0-9]/)
                    ? 'red'
                    : 'black',
                }}
              >
                No mínimo um número
                {hasValidationError(values.password, /[0-9]/) ? (
                  <ClearIcon
                    style={{ color: 'red', marginLeft: 2, fontSize: '18px' }}
                  />
                ) : (
                  <DoneIcon
                    style={{
                      color: '#000FFF',
                      marginLeft: 2,
                      fontSize: '18px',
                    }}
                  />
                )}
              </PasswordCondition>
              <PasswordCondition
                className="mb-0"
                style={{
                  color: hasValidationError(
                    values.password,
                    /[!@#$%^&*(),.?":{}|<>]/,
                  )
                    ? 'red'
                    : 'black',
                }}
              >
                No mínimo um caractere especial (%#@)
                {hasValidationError(
                  values.password,
                  /[!@#$%^&*(),.?":{}|<>]/,
                ) ? (
                  <ClearIcon
                    style={{ color: 'red', marginLeft: 2, fontSize: '18px' }}
                  />
                ) : (
                  <DoneIcon
                    style={{
                      color: '#000FFF',
                      marginLeft: 2,
                      fontSize: '18px',
                    }}
                  />
                )}
              </PasswordCondition>
              <PasswordCondition
                className="mb-0"
                style={{
                  color:
                    values.password !== values.passwordConfirmation ||
                    values.passwordConfirmation === ''
                      ? 'red'
                      : 'black',
                }}
              >
                As senhas devem corresponder
                {values.password !== values.passwordConfirmation ||
                values.passwordConfirmation === '' ? (
                  <ClearIcon
                    style={{ color: 'red', marginLeft: 2, fontSize: '18px' }}
                  />
                ) : (
                  <DoneIcon
                    style={{
                      color: '#000FFF',
                      marginLeft: 2,
                      fontSize: '18px',
                    }}
                  />
                )}
              </PasswordCondition>
              <SignInButton
                type="submit"
                className="mt-4"
                disabled={loading || !areAllConditionsMet(values)}
                style={{
                  backgroundColor:
                    loading || !areAllConditionsMet(values)
                      ? 'gray'
                      : '#000FFF',
                }}
              >
                {loading ? (
                  <LoadingIcon className="fa fa-spinner fa-pulse fa-3x fa-fw" />
                ) : (
                  <span>Criar</span>
                )}
              </SignInButton>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}

export default CreatePassword;
